export const getVideoDuration = (file: File): Promise<number> => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    
    video.onloadedmetadata = () => {
      const duration = video.duration;
      URL.revokeObjectURL(video.src);
      
      if (!Number.isFinite(duration) || duration <= 0) {
        console.warn("Invalid video duration detected:", duration);
        resolve(0);
      } else {
        resolve(Math.floor(duration));
      }
    };
    
    video.onerror = () => {
      console.warn("Could not load video metadata");
      URL.revokeObjectURL(video.src);
      resolve(0);
    };
    
    video.src = URL.createObjectURL(file);
  });
};

// Add other video utility functions here
export const formatVideoDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

export const formatFileSize = (bytes: number): string => {
  const mb = bytes / (1024 * 1024);
  if (mb >= 1000) {
    return `${(mb / 1024).toFixed(1)} GB`;
  }
  return `${mb.toFixed(1)} MB`;
};

export const getVideoFileInfo = async (file: File) => {
  const duration = await getVideoDuration(file);
  const size = formatFileSize(file.size);
  const formattedDuration = formatVideoDuration(duration);
  
  return {
    duration,
    size,
    formattedDuration,
    name: file.name,
    type: file.type
  };
};

// lib/utils/videoUtils.ts
export const compressVideo = (file: File, maxSizeMB: number = 25): Promise<File> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      reject(new Error('Canvas context not available'));
      return;
    }

    video.onloadedmetadata = () => {
      try {
        // ✅ Calculate optimal dimensions
        const maxWidth = 720;
        const maxHeight = 1280;
        
        let { videoWidth, videoHeight } = video;
        
        // Calculate new dimensions if video is too large
        if (videoWidth > maxWidth || videoHeight > maxHeight) {
          const aspectRatio = videoWidth / videoHeight;
          if (videoWidth > videoHeight) {
            videoWidth = maxWidth;
            videoHeight = maxWidth / aspectRatio;
          } else {
            videoHeight = maxHeight;
            videoWidth = maxHeight * aspectRatio;
          }
        }

        canvas.width = videoWidth;
        canvas.height = videoHeight;

        // ✅ Check if compression is needed
        const fileSizeMB = file.size / (1024 * 1024);
        
        if (fileSizeMB <= maxSizeMB) {
          // File is already small enough
          resolve(file);
          return;
        }

        // ✅ For now, we'll create a lower quality version
        // Draw video frame to canvas
        ctx.drawImage(video, 0, 0, videoWidth, videoHeight);
        
        // Convert to blob with lower quality
        canvas.toBlob((blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: 'video/mp4',
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          } else {
            // If compression fails, return original
            resolve(file);
          }
        }, 'image/jpeg', 0.7);
        
      } catch (error) {
        console.warn('Video compression failed:', error);
        resolve(file); // Return original file if compression fails
      }
    };

    video.onerror = () => {
      console.warn('Video compression failed');
      resolve(file); // Return original file if there's an error
    };

    video.src = URL.createObjectURL(file);
  });
};

// ✅ Better video validation
export const validateAndOptimizeVideo = async (file: File): Promise<{
  isValid: boolean;
  optimizedFile: File;
  error?: string;
}> => {
  try {
    // Basic validation
    if (!file.type.startsWith('video/')) {
      return { isValid: false, optimizedFile: file, error: 'Invalid file type' };
    }

    const fileSizeMB = file.size / (1024 * 1024);
    
    // If file is too large, try to compress
    if (fileSizeMB > 100) {
      console.log(`Large file detected (${fileSizeMB.toFixed(1)}MB), attempting optimization...`);
      
      // For now, just reject large files since true compression is complex
      return { 
        isValid: false, 
        optimizedFile: file, 
        error: `File too large (${fileSizeMB.toFixed(1)}MB). Please use a video under 25MB.` 
      };
    }

    return { isValid: true, optimizedFile: file };
  } catch (error) {
    return { isValid: false, optimizedFile: file, error: 'File validation failed' };
  }
};